import { useState, useEffect, useRef } from 'react';

const benefits = [
  {
    icon: 'ri-compass-3-line',
    title: 'Clarity',
    description: 'Stop spinning your wheels and start seeing a real direction for your future.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Confidence',
    description: 'Build belief in yourself and your ability to take meaningful action.',
  },
  {
    icon: 'ri-route-line',
    title: 'Strategy',
    description: 'Get practical guidance and clear next steps tailored to your goals.',
  },
  {
    icon: 'ri-rocket-2-line',
    title: 'Momentum',
    description: 'Move forward faster with support, accountability, and encouragement.',
  },
  {
    icon: 'ri-sparkling-2-line',
    title: 'Transformation',
    description: 'Start becoming the person you know you are meant to be.',
  },
];

export default function BenefitsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="benefits" className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <span
            className="text-xs font-semibold tracking-widest uppercase text-[#D4896A] block mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Benefits
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#2C2C2C]"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            What a Brand New You Can Look Like
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {benefits.map((benefit, i) => (
            <div
              key={benefit.title}
              className={`group bg-[#FAF8F5] rounded-2xl p-8 flex flex-col gap-5 hover:bg-[#D4896A] transition-all duration-400 cursor-default ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms`, transitionDuration: '600ms' }}
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#EDE8E0] group-hover:bg-white/20 transition-colors duration-300">
                <i className={`${benefit.icon} text-2xl text-[#D4896A] group-hover:text-white transition-colors duration-300`} />
              </div>
              {/* Content */}
              <div>
                <h3
                  className="text-xl font-bold text-[#2C2C2C] mb-3 group-hover:text-white transition-colors duration-300"
                  style={{ fontFamily: '\'Playfair Display\', serif' }}
                >
                  {benefit.title}
                </h3>
                <p
                  className="text-sm text-[#6B6B6B] leading-relaxed group-hover:text-white/90 transition-colors duration-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
